<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Das Hinzufügen einer Virtual Tour zu einer Immobilienanzeige ist
            eine der wirkungsvollsten Maßnahmen, die ein Makler ergreifen kann –
            aber nur, wenn es richtig gemacht wird. Eine Tour, die in einer
            unlesbaren URL vergraben oder im falschen Feld hinzugefügt wird,
            wird ignoriert. Dieser Schritt-für-Schritt-Leitfaden deckt jede
            wichtige Plattform und Methode ab: MLS, Zillow, Realtor.com und Ihre
            eigene Website.
          </p>
        </section>

        <section>
          <h2>Schritt 1: Ihre Virtual Tour erstellen</h2>

          <h3>Aufnahmemethode wählen</h3>
          <p>
            Bevor Sie eine Tour zu Ihrer Anzeige hinzufügen können, brauchen Sie
            eine. Der schnellste Ansatz für die meisten Makler ist die
            Verwendung von Travvir oder einer ähnlichen Smartphone-360-App. Für
            einen vollständigen Aufnahmeleitfaden, siehe
            <a href="/blog/de/360-immobilienfotografie-leitfaden"
              >360°-Immobilienfotografie: vollständiger Leitfaden für Makler</a
            >.
          </p>

          <h3>Ihre Tour-URL erhalten</h3>
          <p>
            Sobald Ihre Tour verarbeitet und veröffentlicht ist, stellt Ihre
            Hosting-Plattform einen teilbaren Link bereit. Dies ist die URL, die
            Sie zu jeder Anzeigeplattform hinzufügen. In Travvir finden Sie
            diese unter der „Teilen"-Option der Tour. Testen Sie die URL in
            einem privaten Browser-Fenster, bevor Sie sie zu Ihrer Anzeige
            hinzufügen.
          </p>
        </section>

        <section>
          <h2>Eine Virtual Tour zu MLS-Anzeigen hinzufügen</h2>

          <h3>Das Standard-MLS-Virtual-Tour-URL-Feld</h3>
          <p>
            Die meisten MLS-Systeme enthalten ein dediziertes „Virtual Tour"-
            oder „Tour-URL"-Feld im Anzeigeneditor. Dieses Feld akzeptiert eine
            Standard-HTTPS-URL – fügen Sie Ihren Tour-Link direkt hier ein.
            <strong>MLS-Regeln variieren</strong> je nach Region: Einige
            erlauben jede Virtual-Tour-URL, andere beschränken auf bestimmte
            Plattformformate. Überprüfen Sie die Medienrichtlinien Ihres MLS vor
            der Veröffentlichung.
          </p>

          <h3>RETS- und IDX-Verteilung</h3>
          <p>
            Wenn Sie eine Virtual-Tour-URL zum MLS hinzufügen, wird sie über
            RETS- oder RESO-Datenfeeds an alle IDX-verbundenen Websites
            verteilt.
            <strong
              >Ein einzelner MLS-Eintrag überträgt Ihre Tour automatisch an
              Dutzende von Plattformen</strong
            >.
          </p>

          <h3>MLS-spezifische Anweisungen</h3>
          <p>
            <strong>CRMLS (Kalifornien):</strong> Tour-URL im Feld „Virtual Tour
            1" unter Media eingeben.
            <strong>BRIGHT MLS (Mid-Atlantic):</strong> Tour-URL-Feld im
            Medien-Upload- Bereich unter „Virtual Tour". Unterstützt jeden
            HTTPS-Link. <strong>NTREIS (Nordtexas):</strong> URL muss die Tour
            direkt anzeigen, nicht zu einer Login-Seite weiterleiten.
            <strong>FMLS (Georgia):</strong> URL muss ohne Passwort zugänglich
            sein.
          </p>
        </section>

        <section>
          <h2>Eine Virtual Tour zu Zillow hinzufügen</h2>

          <h3>Methode 1: Über die Zillow 3D Home App</h3>
          <p>
            Die direkteste Zillow-Integration erfolgt über Zillow's eigene App.
            Erstellen Sie eine Zillow 3D Home Tour (wie in unserem
            <a href="/blog/de/zillow-3d-tour-vs-360-virtual-tour"
              >Zillow 3D vs. 360-Tour-Vergleich</a
            >
            beschrieben), die automatisch das 3D-Tour-Badge zu Ihrer
            Zillow-Anzeige hinzufügt.
          </p>

          <h3>Methode 2: Manuelle URL-Hinzufügung über Zillow Premier Agent</h3>
          <p>
            Wenn Sie eine Nicht-Zillow-Tour (Travvir, Matterport usw.) auf
            Zillow präsentieren möchten: Melden Sie sich bei Ihrem Zillow
            Premier Agent-Konto an → Anzeige finden → „Anzeige bearbeiten" → Zum
            Media-Bereich scrollen → Tour-URL im dafür vorgesehenen Feld
            hinzufügen.
            <strong>Die Hybrid-Strategie:</strong> Zillow 3D Home Tour für das
            Badge erstellen, plus Travvir-Tour-URL für vollwertige Betrachtung
            überall sonst.
          </p>
        </section>

        <section>
          <h2>Eine Virtual Tour zu Realtor.com hinzufügen</h2>

          <h3>Über das Listing-Agent-Portal</h3>
          <p>
            Im Realtor.com-Anzeigenverwaltungsportal: Anzeige auswählen → Media.
            Im Virtual-Tour-Bereich Ihre Tour-URL einfügen. Realtor.com
            akzeptiert jeden Standard-HTTPS-Virtual-Tour-Link.
            <strong
              >Realtor.com verteilt an Move.com, Homes.com und
              Partner-Portale</strong
            >.
          </p>
        </section>

        <section>
          <h2>Eine Virtual Tour zu Ihrer Agentur-Website hinzufügen</h2>

          <h3>Einbettungscode verwenden (iFrame)</h3>
          <p>
            Die meisten Virtual-Tour-Plattformen stellen einen Einbettungscode
            bereit – ein kleines HTML-Snippet, das Sie in Ihren
            Website-Seiteneditor einfügen. In Travvir finden Sie den
            Einbettungscode unter Tour → „Teilen" → „Einbetten".
          </p>
          <p>
            Der Einbettungscode sieht so aus:
            <code
              >&lt;iframe src="[Ihre-Tour-URL]" width="100%" height="600"
              frameborder="0" allowfullscreen&gt;&lt;/iframe&gt;</code
            >
          </p>
          <p>
            Für WordPress-Websites: in den HTML-Editor-Block einfügen. Für
            Squarespace: Embed-Block verwenden. Für Wix: HTML-Einbettungselement
            verwenden.
          </p>

          <h3>Responsive Größenanpassung</h3>
          <p>
            Stellen Sie die iFrame-Breite auf 100 % (nicht eine feste
            Pixelbreite) und Höhe auf mindestens 500 px für mobilfreundliche
            Anzeige ein. Testen Sie Ihre Einbettung auf Desktop und Mobilgerät,
            bevor die Anzeige live geht.
          </p>
        </section>

        <section>
          <h2>Virtual Tours zu E-Mail-Marketing hinzufügen</h2>
          <p>
            E-Mail-Clients können iFrames oder interaktive Inhalte nicht rendern
            – versuchen Sie nie, eine echte 360°-Tour in eine E-Mail
            einzubetten. Stattdessen einen Screenshot oder Thumbnail-Bild der
            Tour einfügen, das zur gehosteten Tour-URL verlinkt. Tour-Links in
            E-Mail-Marketing erzielen
            <strong>2,8× höhere Klickraten</strong> als
            Standard-Fotogalerie-Links.
          </p>
        </section>

        <section>
          <h2>Fazit: Dafür sorgen, dass Ihre Tour gesehen wird</h2>
          <p>
            Das Hinzufügen einer Virtual Tour zu Ihrer Immobilienanzeige
            erfordert ein paar Minuten mehr, liefert aber überproportionale
            Ergebnisse. Der Schlüssel ist,
            <strong
              >Ihre Tour-URL zu jeder verfügbaren Plattform hinzuzufügen</strong
            >
            – MLS, Zillow, Realtor.com, Ihre Agentur-Website und Ihre
            E-Mail-Kampagnen.
          </p>
          <p>
            Eine Tour-URL, richtig verteilt, kann jeden Käufer auf jeder großen
            Plattform gleichzeitig erreichen. Das ist der effizienteste
            Marketing-Hebel, der Immobilienmaklern heute zur Verfügung steht.
          </p>
        </section>
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/virtual-tour-immobilien-erstellen">Virtual Tour Immobilien erstellen : Anleitung</a></li>
            <li><a href="/blog/de/360-virtual-tour-website-einbetten">360°-Virtual-Tour in Website einbetten</a></li>
            <li><a href="/blog/de/virtual-tour-seo-leitfaden">Virtual Tour SEO : Rankings verbessern</a></li>
            <li><a href="/blog/de/makler-virtual-tour-2026">Makler Virtual Tour 2026: Leitfaden für Immobilienmakler</a></li>
            <li><a href="/blog/de/beste-virtual-tour-software-immobilien">Beste Virtual-Tour-Software Immobilien</a></li>
            <li><a href="/blog/de/virtual-tours-vs-immobilienfotos">Virtual Tours vs. Immobilienfotos : Was wirkt besser?</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "virtual-tour-zu-immobilienanzeige-hinzufuegen",
);

useBlogSeo({
  title: "Virtual Tour zur Immobilienanzeige [2026] hinzufügen | Travvir",
  description:
    "So fügen Sie eine Virtual Tour zu Ihrer Immobilienanzeige hinzu. Immobilienportale, MLS und Social Media – vollständige Anleitung.",
  slug: "virtual-tour-zu-immobilienanzeige-hinzufuegen",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
